import { Box, textStyles, theme } from "@pallet-hq/hegel"
import styled from "@emotion/styled"
import { CSSProperties, MouseEvent, useEffect, useRef, useState } from "react"
import UnstyledButton from "components/Buttons/UnstyledButton"
import { useBillingCycleSelect } from "./hook"

export const BillingCycleSelector = () => {
    const { billingCycle, setBillingCycle, possibleCycles } =
        useBillingCycleSelect()
    const [buttonBoundingBox, setButtonBoundingBox] = useState<DOMRect>()
    const [wrapperBoundingBox, setWrapperBoundingBox] = useState<DOMRect>()
    const [highlightedCycle, setHighlightedCycle] = useState<string>()
    const [isHoveredFromNull, setIsHoveredFromNull] = useState(true)

    const initialCycleRef = useRef<HTMLDivElement>(null)
    const highlightRef = useRef<HTMLDivElement>(null)
    const wrapperRef = useRef<HTMLDivElement>(null)

    const repositionHighlight = (e: MouseEvent, cycle: string) => {
        if (!wrapperRef.current) return

        const target = e.target as HTMLElement
        setButtonBoundingBox(target.getBoundingClientRect())
        setWrapperBoundingBox(wrapperRef.current.getBoundingClientRect())
        setIsHoveredFromNull(!highlightedCycle)
        setHighlightedCycle(cycle)
    }

    const resetHighlight = (isHoveredFromNull: boolean) => {
        if (!initialCycleRef.current || !wrapperRef.current) return

        setButtonBoundingBox(initialCycleRef.current.getBoundingClientRect())
        setWrapperBoundingBox(wrapperRef.current.getBoundingClientRect())
        setIsHoveredFromNull(isHoveredFromNull)
        setHighlightedCycle(billingCycle)
    }

    useEffect(() => {
        resetHighlight(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialCycleRef, wrapperRef, billingCycle])

    const highlightStyles = {
        opacity: highlightedCycle ? 1 : 0,
    } as CSSProperties
    if (buttonBoundingBox && wrapperBoundingBox) {
        highlightStyles.transitionDuration = isHoveredFromNull ? "0ms" : "150ms"
        highlightStyles.width = `${buttonBoundingBox.width}px`
        highlightStyles.transform = `translate(${
            buttonBoundingBox.left - wrapperBoundingBox.left
        }px)`
    }

    return (
        <Box display="flex" flexDirection="column" width="fit-content">
            <ButtonRowWrapper>
                <ButtonRow
                    ref={wrapperRef}
                    onMouseLeave={() => resetHighlight(false)}
                >
                    <CycleHighlight
                        ref={highlightRef}
                        style={highlightStyles}
                    />
                    {possibleCycles.map((cycle, index) => (
                        <div
                            key={index}
                            ref={
                                cycle === billingCycle
                                    ? initialCycleRef
                                    : undefined
                            }
                        >
                            <StyledButton
                                onMouseOver={(e: MouseEvent) =>
                                    repositionHighlight(e, cycle)
                                }
                                onClick={() => setBillingCycle(cycle)}
                                selected={cycle === billingCycle}
                            >
                                {cycle}
                            </StyledButton>
                        </div>
                    ))}
                </ButtonRow>
            </ButtonRowWrapper>
        </Box>
    )
}

const ButtonRowWrapper = styled.div`
    display: flex;
    align-items: center;
    background-color: ${theme.colors.gray200};
    border-radius: 1000px;
    padding: 4px;
`

const ButtonRow = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`

const CycleHighlight = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    border-radius: 44px;
    height: 28px;
    transition: 0.15s ease;
    transition-property: width, transform, opacity;
    background-color: ${theme.colors.gray300};
`

const StyledButton = styled(UnstyledButton)<{ selected: boolean }>`
    ${textStyles.uiTiny500};
    display: inline-block;
    position: relative;
    padding: 4px;
    min-width: 95px;
    height: 28px;
    border-radius: 104px;
    color: ${theme.colors.gray600};
    transition: color 250ms;
    ${({ selected }) =>
        selected &&
        `
            color: white;
            background: ${theme.colors.gray900};
            /* Elevation/Level 0 */
            box-shadow: 0px 2px 1px rgba(0, 0, 0, 0.01), 0px 1px 1px rgba(0, 0, 0, 0.03), 0px 1px 1px rgba(0, 0, 0, 0.04), 0px 0px 0px rgba(0, 0, 0, 0.05), 0px 0px 0px rgba(0, 0, 0, 0.05);
        `};
`
